import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { useLangParam } from '@/shared/hooks'

interface SEOProps {
  titleKey?: string
  descriptionKey?: string
  noIndex?: boolean
}

export function SEO({ titleKey, descriptionKey, noIndex = false }: SEOProps) {
  const { t } = useTranslation()
  const { lang } = useLangParam()
  const location = useLocation()

  const baseUrl = 'https://heynicolai.com'
  const currentPath = location.pathname.replace(/^\/(en|fr)/, '')

  const title = titleKey ? t(titleKey) : t('seo.defaultTitle')
  const description = descriptionKey ? t(descriptionKey) : t('seo.defaultDescription')

  const canonicalUrl = `${baseUrl}/${lang}${currentPath}`
  const alternateEn = `${baseUrl}/en${currentPath}`
  const alternateFr = `${baseUrl}/fr${currentPath}`

  return (
    <Helmet>
      {/* Document language */}
      <html lang={lang} />

      {/* Title and description */}
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang for multilingual SEO */}
      <link rel="alternate" hrefLang="en" href={alternateEn} />
      <link rel="alternate" hrefLang="fr" href={alternateFr} />
      <link rel="alternate" hrefLang="x-default" href={alternateEn} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={lang === 'fr' ? 'fr_FR' : 'en_US'} />
      <meta property="og:locale:alternate" content={lang === 'fr' ? 'en_US' : 'fr_FR'} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="HeyNicolai" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />

      {/* No index if requested */}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
    </Helmet>
  )
}
